export type AuthErrorKind =
  | 'invalid-credentials'
  | 'invalid-email'
  | 'email-undeliverable'
  | 'password-required'
  | 'email-not-eligible'
  | 'registration-unavailable'
  | 'invalid-code'
  | 'expired-code'
  | 'delivery-failed'
  | 'password-policy'
  | 'throttled'
  | 'locked-out'
  | 'network'
  | 'service-unavailable'
  | 'reset-required'
  | 'session-expired'
  | 'passkey-not-completed'
  | 'unsupported-challenge'
  | 'unknown';

export class AuthFlowError extends Error {
  constructor(
    public readonly kind: AuthErrorKind,
    options?: { cause?: unknown },
  ) {
    super(kind);
    if (options?.cause !== undefined) {
      Object.defineProperty(this, 'cause', { value: options.cause });
    }
    this.name = 'AuthFlowError';
  }
}

const ERROR_COPY: Record<AuthErrorKind, string> = {
  'invalid-credentials': 'Incorrect email and/or password.',
  'invalid-email': 'Enter a valid email address.',
  'email-undeliverable':
    "We couldn't create an account with this email address. Check it and try again.",
  'password-required': 'Enter your password.',
  'email-not-eligible':
    'We couldn’t continue with this email right now. Please try again later or contact support.',
  'registration-unavailable': 'Something went wrong. Please try again later or contact support.',
  'invalid-code': 'The code you entered is incorrect.',
  'expired-code': 'This code has expired. Request a new code and try again.',
  'delivery-failed': "We couldn't send the code. Try again.",
  'password-policy':
    'Use at least 12 characters with uppercase, lowercase, a number, and a symbol.',
  throttled:
    'Too many attempts right now. You can try again, but another attempt may also be limited.',
  'locked-out': 'Too many attempts. Login is temporarily paused. Please try again later.',
  network: "We couldn't reach the authentication service. Check your connection and try again.",
  'service-unavailable':
    "We're having trouble on our end. Please try again in a few minutes or contact support.",
  'reset-required': 'Reset your password to continue.',
  'session-expired': 'Your session has expired. Please sign in again.',
  'passkey-not-completed': "Passkey sign-in wasn't completed. Try again or use password.",
  'unsupported-challenge':
    'This sign-in needs additional help. Contact support or use another account.',
  unknown: 'Authentication failed. Try again.',
};

const AUTH_ERROR_KIND_BY_NAME: Record<string, AuthErrorKind> = {
  NotAuthorizedException: 'invalid-credentials',
  UserNotFoundException: 'invalid-credentials',
  UsernameExistsException: 'registration-unavailable',
  AliasExistsException: 'registration-unavailable',
  CodeMismatchException: 'invalid-code',
  ExpiredCodeException: 'expired-code',
  CodeDeliveryFailureException: 'delivery-failed',
  InvalidPasswordException: 'password-policy',
  TooManyRequestsException: 'throttled',
  LimitExceededException: 'throttled',
  PasswordResetRequiredException: 'reset-required',
  UnexpectedLambdaException: 'service-unavailable',
  InternalErrorException: 'service-unavailable',
};

export const getAuthErrorMessage = (error: AuthFlowError): string => ERROR_COPY[error.kind];

export const cognitoErrorName = (error: unknown): string | undefined =>
  (error as { name?: string } | undefined)?.name;

export const isCognitoLockout = (error: unknown): boolean =>
  cognitoErrorName(error) === 'NotAuthorizedException' &&
  (error as { message?: unknown }).message === 'Password attempts exceeded';

export const mapCognitoAuthError = (error: unknown): AuthFlowError => {
  const name = cognitoErrorName(error);
  if (isCognitoLockout(error)) {
    return new AuthFlowError('locked-out', { cause: error });
  }
  if (name === 'UserLambdaValidationException') {
    const message = (error as { message?: unknown }).message;
    const kind =
      typeof message === 'string' &&
      message.includes(
        'Email validation failed: the email address appears to be invalid or undeliverable.',
      )
        ? 'email-undeliverable'
        : 'service-unavailable';
    return new AuthFlowError(kind, { cause: error });
  }
  if (name && AUTH_ERROR_KIND_BY_NAME[name]) {
    return new AuthFlowError(AUTH_ERROR_KIND_BY_NAME[name], { cause: error });
  }
  if (error instanceof TypeError || name === 'NetworkError') {
    return new AuthFlowError('network', { cause: error });
  }
  return new AuthFlowError('unknown', { cause: error });
};

export const normalizeCognitoUsername = (email: string): string => email.trim().toLowerCase();
